import type { FC } from "react"
import React from "react"

import type { Rectangle2d } from "@logic/lib/2d"
import {
  doesPolygon,
  isSamePoint,
  foreshortenScaleIsomorphic,
} from "@logic/lib/2d"
import type { InstallArea } from "@logic/lib/design"
import type { Face2dComputed } from "@logic/lib/mesh"
import { useRecoilValue, selector, atom } from "recoil"

import { areaIndex, projectFace, findAreaState } from "../../state/elements"
import type { TransactionOperation } from "../../state/elements/transactions"
import {
  getMousePositionOnScreen,
  readonlyTransactors,
} from "../../state/elements/transactions"
import { getOnscreenPanelFootprint } from "../../state/elements/transactions/getOnscreenPanelFootprint"
import { startPointState } from "../../state/inputs"
import { getTool } from "../../state/tool"

export const shouldShowPreviewState = atom<boolean>({
  key: `shouldShowPreview`,
  default: true,
})

export const getHoveredInstallArea: TransactionOperation<
  undefined,
  (Face2dComputed & InstallArea & { id: string }) | null
> = ({ get }) => {
  const mouse = getMousePositionOnScreen(readonlyTransactors(get))
  const installAreaIds = get(areaIndex)
  const hoveredInstallArea = installAreaIds
    .map((id) => projectFace(get, id, findAreaState))
    .find((area) => doesPolygon(area).containPoint(mouse))

  return hoveredInstallArea ?? null
}

export const getPanelPreview: TransactionOperation<
  undefined,
  (Rectangle2d & { orientation: number }) | null
> = ({ get }) => {
  const readonly = readonlyTransactors(get)
  const mouse = getMousePositionOnScreen(readonly)
  const startPoint = get(startPointState)
  const tool = getTool(readonly)
  const isUsingPanelCreator = [
    `PanelCreatorPortrait`,
    `PanelCreatorLandscape`,
  ].includes(tool)
  const isDragging = startPoint && !isSamePoint(startPoint, mouse)
  if (
    get(shouldShowPreviewState) === false ||
    !isUsingPanelCreator ||
    isDragging
  ) {
    return null
  }
  const isLandscape = tool === `PanelCreatorLandscape`
  const hoveredInstallArea = getHoveredInstallArea(readonly)
  const moduleScale = getOnscreenPanelFootprint(readonly)
  const degreesTiltedFromSurface = hoveredInstallArea?.slope ?? 0
  const moduleScaleInPerspective = foreshortenScaleIsomorphic({
    w: moduleScale.w,
    h: moduleScale.h,
    degreesTiltedFromSurface,
    axis: isLandscape ? `x` : `y`,
  })

  const orientation = isLandscape ? 90 : 0
  const r = Math.round(hoveredInstallArea?.azimuth ?? 0) + orientation
  const { x, y } = mouse

  return {
    ...moduleScaleInPerspective,
    x,
    y,
    r,
    orientation,
  }
}

const panelPreviewState = selector<Rectangle2d | null>({
  key: `panelPreview`,
  get: ({ get }) => {
    const transactors = readonlyTransactors(get)

    return getPanelPreview(transactors)
  },
})

const PreviewPanel_Internal: FC = () => {
  const panel = useRecoilValue(panelPreviewState)

  if (panel === null) return null

  const { x, y, w, h, r } = panel

  return panel === null ? null : (
    <rect
      x={x}
      y={y}
      width={w}
      height={h}
      fill="#000"
      style={{ transformBox: `fill-box`, pointerEvents: `none` }}
      transform={`rotate(${r})`}
    />
  )
}

export const PreviewPanel = React.memo(PreviewPanel_Internal)
